import React, {useEffect, useRef, useState} from "react";
import {Typography} from "@mui/material";
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';

/**
 * 화면 맨 위에서 아래로 끌어당겨 새로고침.
 *
 * 이 앱은 문서(window)가 스크롤된다(ScrollToTop 이 window.scrollTo 를 쓴다).
 * 그래서 스크롤 위치는 window 기준으로 본다.
 *
 * 내용을 밀어내는 데 transform 을 쓰지 않고 **표시 영역의 height** 를 키운다.
 * transform 은 값이 0 이어도 자식 `position: fixed` 의 기준을 바꿔 버리는데,
 * 기록 화면 상단 로고가 fixed 라서 그 방식은 로고를 망가뜨린다.
 */

/** 이 거리(px)만큼 끌어야 새로고침이 실행된다 */
const THRESHOLD = 70;
/** 더 끌어도 표시는 여기까지만 내려온다 */
const MAX_PULL = 110;
/** 손가락 이동 대비 실제로 내려오는 비율 (고무줄 느낌) */
const RESISTANCE = 0.5;
/** 너무 빨리 끝나면 눌린 건지 알 수 없어, 표시는 최소 이만큼 유지한다 */
const MIN_SPIN_MS = 600;

const scrollTop = () =>
  window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

const PullToRefresh = (props: { onRefresh: () => void | Promise<void>, children: React.ReactNode }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const pullRef = useRef(0);
  const busyRef = useRef(false);
  const onRefreshRef = useRef(props.onRefresh);

  const [pull, setPull] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // 리스너를 한 번만 붙이므로(아래 effect) 최신 콜백은 ref 로 넘긴다
  useEffect(() => {
    onRefreshRef.current = props.onRefresh;
  });

  // 브라우저 기본 '당겨서 새로고침'을 이 화면에서만 끈다 (안드로이드 크롬)
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.style.overscrollBehaviorY;
    root.style.overscrollBehaviorY = 'contain';
    return () => {
      root.style.overscrollBehaviorY = prev;
    };
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (el === null) {
      return;
    }

    const setPullValue = (value: number) => {
      pullRef.current = value;
      setPull(value);
    };

    const cancel = () => {
      startYRef.current = null;
      setDragging(false);
      setPullValue(0);
    };

    const onStart = (e: TouchEvent) => {
      if (busyRef.current || e.touches.length !== 1 || scrollTop() > 0) {
        return;
      }
      startYRef.current = e.touches[0].clientY;
    };

    const onMove = (e: TouchEvent) => {
      if (startYRef.current === null || busyRef.current) {
        return;
      }
      const delta = e.touches[0].clientY - startYRef.current;
      // 위로 올리는 중이거나 이미 스크롤이 내려갔으면 평범한 스크롤로 돌려준다
      if (delta <= 0 || scrollTop() > 0) {
        cancel();
        return;
      }
      // 기본 동작(고무줄 스크롤·브라우저 새로고침)을 막는다.
      // React 의 onTouchMove 는 passive 라 preventDefault 가 먹지 않아
      // 이 리스너를 passive:false 로 직접 붙였다.
      e.preventDefault();
      setDragging(true);
      setPullValue(Math.min(MAX_PULL, delta * RESISTANCE));
    };

    const onEnd = () => {
      if (startYRef.current === null || busyRef.current) {
        return;
      }
      startYRef.current = null;
      setDragging(false);

      if (pullRef.current < THRESHOLD) {
        setPullValue(0);
        return;
      }

      busyRef.current = true;
      setRefreshing(true);
      setPullValue(THRESHOLD);

      const done = () => {
        busyRef.current = false;
        setRefreshing(false);
        setPullValue(0);
      };
      const minSpin = new Promise((resolve) => { window.setTimeout(resolve, MIN_SPIN_MS); });
      // 새로고침이 실패해도 표시는 반드시 걷는다
      Promise.all([Promise.resolve(onRefreshRef.current()), minSpin]).then(done, done);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  const ready = pull >= THRESHOLD;

  return (
    <Box ref={rootRef}>
      {/* 상단 로고가 fixed(zIndex 999)라 그 위에 그린다 */}
      <Box sx={{
        position: 'relative', zIndex: 1300,
        height: pull, overflow: 'hidden',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        transition: dragging ? 'none' : 'height .25s ease',
      }}>
        <Indicator>
          {refreshing ? (
            <CircularProgress size={22} sx={{ color: '#59b544' }} />
          ) : (
            <ArrowDownwardRoundedIcon sx={{
              fontSize: 24, color: '#59b544',
              transition: 'transform .2s',
              transform: ready ? 'rotate(180deg)' : 'none',
            }} />
          )}
          <Typography sx={{ fontSize: 11, mt: .3, color: '#888', whiteSpace: 'nowrap' }}>
            {refreshing ? '새로고침 중' : ready ? '놓으면 새로고침' : '당겨서 새로고침'}
          </Typography>
        </Indicator>
      </Box>

      {props.children}
    </Box>
  );
}

/** 표시 아이콘 + 문구 묶음 */
const Indicator = (props: { children: React.ReactNode }) => (
  <Box sx={{ pb: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    {props.children}
  </Box>
);

export default PullToRefresh;
